import React, { useEffect, useMemo, useState } from 'react';
import { useUser } from '@/contexts/UserContext';
import { SectionLayout } from '@/components/shared/section-layout';
import { GenericTable, type GenericTableColumn } from '@/styles/GenericTable';
import { GenericTag } from '@/styles/GenericTag';
import { NewItemButton, SearchButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import { ChangeOrder, NewChangeOrderInput } from './types';
import { useChangeOrders, fetchChangeOrderById } from './store';
import { useOpenEntityRecord } from '@/hooks/useOpenEntity';
import { fetchProjectLookup, fetchTeamMemberLookup } from '@/lib/entity-api';
import { exportEntityCsv } from '@/lib/csv';
import { ChangeOrderForm } from './components/ChangeOrderForm';
import { trackChangeOrderCreated, trackChangeOrderUpdated, trackButtonClick, trackFormSubmission } from '@/lib/posthog-events';
import { Copy, Trash2, Download, Upload } from 'lucide-react';
import { getApiUrl } from '@/lib/api-config';

export function ChangeOrdersPage() {
  const { companyId, user } = useUser();
  const { items, refresh, addChangeOrder, updateChangeOrder, exportRows, setSearchInput, queryKey, tableProps } = useChangeOrders();
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<ChangeOrder | null>(null);
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([]);
  const projectIdToName = useMemo(() => new Map(projects.map((p) => [p.id, p.name] as const)), [projects]);
  const [currentAuthor, setCurrentAuthor] = useState<string>("Unknown User");
  // a link from another document (Linked Docs) opens this change order
  useOpenEntityRecord('change_order', fetchChangeOrderById, (co) => { setEditing(co); setEditOpen(true); });
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  useEffect(() => {
    if (companyId && user) {
      (async () => {
        try {
          const [projData, members] = await Promise.all([fetchProjectLookup(), fetchTeamMemberLookup()]);
          setProjects(projData);
          const fullNameFromTable = members.find((m) => m.user_id === user.id)?.full_name || '';
          const fullNameFromMeta = (user.user_metadata as any)?.full_name as string | undefined;
          setCurrentAuthor(fullNameFromTable || fullNameFromMeta || user.email || 'Unknown User');
        } catch (error) {
          console.error('Error loading change order options:', error);
        }
      })();
    }
  }, [companyId, user]);

  // The list is a new query when the project, the search or the sort changes: rows selected in the previous one are gone.
  useEffect(() => {
    setSelectedRows([]);
  }, [queryKey]);

  // co_number is a server-generated business number ("CO000001"), shown as-is.
  const formatCoNumber = (coNumber: string): string => coNumber;

  // Map change order statuses to GenericTag status types
  const mapChangeOrderStatusToTag = (status: string): 'draft' | 'sent' | 'approved' | 'rejected' | 'cancelledChangeOrder' => {
    switch (status.toLowerCase()) {
      case 'draft':
        return 'draft';
      case 'pending_approval':
      case 'pending':
      case 'submitted':
        return 'sent'; // Blue for pending/submitted
      case 'approved':
        return 'approved'; // Green for approved
      case 'rejected':
      case 'voided': // Legacy support
        return 'rejected'; // Red for rejected
      case 'cancelled':
        return 'cancelledChangeOrder'; // Orange for cancelled (change orders only)
      default:
        return 'draft';
    }
  };

  // Format status text with proper capitalization
  const formatChangeOrderStatusText = (status: string): string => {
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  async function handleCreate(data: NewChangeOrderInput) {
    try {
      const created = await addChangeOrder(data, currentAuthor);
      
      if (created) {
        // Track successful change order creation with PostHog
        trackChangeOrderCreated({
          change_order_id: created.id,
          co_number: created.coNumber,
          title: data.title,
          project_id: data.projectId,
          client_cost: data.clientCost,
          builder_cost: data.builderCost,
          status: data.status,
          approval_deadline: data.approvalDeadline,
          company_id: companyId,
          created_by: user?.id
        });

        // Track successful form submission
        trackFormSubmission({
          form_type: 'change_order_creation',
          success: true
        });
      }
      
      return created;
    } catch (error) {
      // Track failed form submission with PostHog
      trackFormSubmission({
        form_type: 'change_order_creation',
        success: false,
        error_message: error instanceof Error ? error.message : 'Failed to create change order'
      });
      
      throw error;
    }
  }

  async function handleEdit(data: NewChangeOrderInput) {
    if (editing) {
      try {
        const updated = await updateChangeOrder(editing.id, data);
        
        if (updated) {
          // Track successful change order update with PostHog
          trackChangeOrderUpdated({
            change_order_id: editing.id,
            co_number: updated.coNumber,
            title: data.title,
            project_id: data.projectId,
            client_cost: data.clientCost,
            builder_cost: data.builderCost,
            status: data.status,
            approval_deadline: data.approvalDeadline,
            company_id: companyId,
            updated_by: user?.id
          });

          // Track successful form submission
          trackFormSubmission({
            form_type: 'change_order_update',
            success: true
          });
        }
        
        return updated;
      } catch (error) {
        // Track failed form submission with PostHog
        trackFormSubmission({
          form_type: 'change_order_update',
          success: false,
          error_message: error instanceof Error ? error.message : 'Failed to update change order'
        });
        
        throw error;
      }
    }
    return undefined;
  }

  // Handle copy selected change orders
  const handleCopySelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one change order to copy.');
      return;
    }

    if (!companyId || !user?.id) {
      alert('Please log in again to copy change orders');
      return;
    }

    try {
      const selectedChangeOrders = items.filter(co => selectedRows.includes(co.id));

      // Copy each selected change order - let API generate co_number automatically
      for (const changeOrder of selectedChangeOrders) {
        const response = await fetch(getApiUrl('api/entities/change_order'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: user.id,
            company_id: companyId,
            // Don't include co_number - let API generate it
            project_id: changeOrder.projectId,
            title: changeOrder.title,
            description: changeOrder.description,
            client_cost: changeOrder.clientCost,
            builder_cost: changeOrder.builderCost,
            approval_deadline: changeOrder.approvalDeadline || null,
            status: 'draft', // Always set to draft for copies
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Error copying change order:', errorData);
          continue;
        }
      }

      await refresh();
      setSelectedRows([]);
      alert(`Successfully copied ${selectedChangeOrders.length} change order(s).`);
    } catch (error) {
      console.error('Error copying change orders:', error);
      alert('Error copying change orders. Please try again.');
    }
  };

  // Handle delete selected change orders
  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one change order to delete.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} change order(s)? This action cannot be undone.`)) {
      return;
    }

    if (!companyId || !user?.id) {
      alert('Please log in again to delete change orders');
      return;
    }

    try {
      for (const changeOrderId of selectedRows) {
        const response = await fetch(getApiUrl(`api/entities/change_order/${changeOrderId}`), {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: user.id,
            company_id: companyId
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Error deleting change order:', errorData);
          continue;
        }
      }

      await refresh();
      setSelectedRows([]);
      alert(`Successfully deleted ${selectedRows.length} change order(s).`);
    } catch (error) {
      console.error('Error deleting change orders:', error);
      alert('Error deleting change orders. Please try again.');
    }
  };

  // Export to CSV: the rows selected in the table, or -- with nothing selected -- EVERY change order of the current
  // project + search, read by the backend (not only the pages loaded in the table).
  const handleExportSelected = () => exportEntityCsv(exportRows, selectedRows, 'change-orders', 'change orders');

  // Define table columns (placed after all handler functions to avoid hoisting issues)
  const columns: GenericTableColumn<ChangeOrder>[] = [
    {
      key: 'coNumber',
      header: 'CO ID',
      width: '6.5rem',
      sortable: true,
      sortKey: 'co_number',
      render: (row) => <span className="font-medium">{formatCoNumber(row.coNumber)}</span>,
    },
    {
      key: 'title',
      header: 'Title',
      width: '12.5rem', // 200px = 12.5rem
      sortable: true,
      sortKey: 'title',
      render: (row) => <span className="font-medium">{row.title}</span>,
    },
    {
      key: 'projectId',
      header: 'Project',
      width: '9.375rem', // 150px = 9.375rem
      sortable: true,
      sortKey: 'project',
      render: (row) => row.projectName ?? projectIdToName.get(row.projectId) ?? '',
    },
    {
      key: 'approvalDeadline',
      header: 'Approval Deadline',
      width: '9.375rem',
      sortable: true,
      sortKey: 'approval_deadline',
      render: (row) => row.approvalDeadline ? new Date(row.approvalDeadline).toLocaleDateString() : '-',
    },
    {
      key: 'status',
      header: 'Status',
      width: '8.75rem',
      sortable: true,
      sortKey: 'status',
      render: (row) => (
        <GenericTag 
          type="status" 
          value={mapChangeOrderStatusToTag(row.status)}
        >
          {formatChangeOrderStatusText(row.status)}
        </GenericTag>
      ),
    },
    {
      key: 'clientCost',
      header: 'Client Cost',
      width: '8.125rem',
      sortable: true,
      sortKey: 'client_cost',
      render: (row) => `$${Number(row.clientCost ?? 0).toLocaleString()}`,
    },
    {
      key: 'builderCost',
      header: 'Builder Cost',
      width: '8.125rem',
      sortable: true,
      sortKey: 'builder_cost',
      render: (row) => `$${Number(row.builderCost ?? 0).toLocaleString()}`,
    },
    {
      key: 'attachments',
      header: 'Attachments',
      width: '6.25rem',
      align: 'center' as const,
      render: (row) => row.attachment_count ?? 0,
    },
    {
      key: 'commentsCount',
      header: 'Comments',
      width: '6.25rem',
      align: 'center' as const,
      render: (row) => row.commentsCount ?? 0,
    },
  ];

  const menuItems = [
    {
      label: 'Copy',
      icon: <Copy className="w-4 h-4" />,
      onClick: handleCopySelected,
    },
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDeleteSelected,
      destructive: true,
    },
    {
      label: 'Import',
      icon: <Upload className="w-4 h-4" />,
      onClick: () => console.log('Import clicked'),
    },
    {
      label: 'Export',
      icon: <Download className="w-4 h-4" />,
      onClick: handleExportSelected,
    },
  ];

  return (
    <SectionLayout title="Change Orders">
      <div className="space-y-6">
        {/* Action Buttons */}
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-2">
            <NewItemButton
              label="New"
              onClick={() => {
                trackButtonClick('new_change_order_button', { context: 'change_orders' });
                setCreateOpen(true);
              }}
            />
            <SearchButton
              onSearch={setSearchInput}
              placeholder="Search"
            />
            <ThreeDotMenuButton items={menuItems} />
          </div>
        </div>

        {/* Generic Table */}
        <GenericTable
          columns={columns}
          {...tableProps}
          rowKey={(row) => row.id}
          scrollContainerMaxWidth="calc(100vw - 19.2rem)"
          minTableWidth={1200}
          onRowClick={(item) => { setEditing(item); setEditOpen(true); }}
          selectable={true}
          selectedKeys={selectedRows}
          onSelectionChange={setSelectedRows}
        />

        <ChangeOrderForm
          mode="create"
          initialData={null}
          onCancel={() => setCreateOpen(false)}
          onSubmit={handleCreate}
          onUpdateCreated={(id, data) => updateChangeOrder(id, data)}
          projects={projects}
          isOpen={createOpen}
          onRelatedDataChanged={refresh}
        />

        <ChangeOrderForm
          mode="edit"
          initialData={editing ?? null}
          onCancel={() => { setEditOpen(false); setEditing(null); }}
          onSubmit={handleEdit}
          projects={projects}
          isOpen={editOpen}
          onRelatedDataChanged={refresh}
        />
      </div>
    </SectionLayout>
  );
}